import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { unwrapApiArray } from "@/lib/api-response";

// Default badge seed data for demo mode
// icon values map to Lucide component names (see BadgeDisplay.tsx getIconComponent)
const DEFAULT_BADGES = [
  {
    id: 1,
    name: "First Log",
    description: "Submitted your first work log",
    icon: "check-circle",
    criteria_type: "milestone",
    criteria_value: 1,
  },
  {
    id: 2,
    name: "10 Hours",
    description: "Logged 10 hours of work experience",
    icon: "clock",
    criteria_type: "hours",
    criteria_value: 10,
  },
  {
    id: 3,
    name: "50 Hours",
    description: "Logged 50 hours of work experience",
    icon: "trophy",
    criteria_type: "hours",
    criteria_value: 50,
  },
  {
    id: 4,
    name: "100 Hours",
    description: "Logged 100 hours — halfway to completion!",
    icon: "star",
    criteria_type: "hours",
    criteria_value: 100,
  },
  {
    id: 5,
    name: "Field Complete",
    description: "Completed 300 hours of field experience",
    icon: "award",
    criteria_type: "hours",
    criteria_value: 300,
  },
  {
    id: 6,
    name: "Top Performer",
    description: "Received a 5-star rating from a supervisor",
    icon: "star",
    criteria_type: "rating",
    criteria_value: 5,
  },
  {
    id: 7,
    name: "First Tip",
    description: "Received your first tip from a partner",
    icon: "heart",
    criteria_type: "tips",
    criteria_value: 1,
  },
  {
    id: 8,
    name: "Community Guide",
    description: "Shared 5 helpful tips with the community",
    icon: "heart",
    criteria_type: "tips",
    criteria_value: 5,
  },
  {
    id: 9,
    name: "Task Master",
    description: "Completed 10 work log tasks",
    icon: "check",
    criteria_type: "tasks",
    criteria_value: 10,
  },
  {
    id: 10,
    name: "Rising Star",
    description: "Earned a badge in your first week",
    icon: "trending-up",
    criteria_type: "milestone",
    criteria_value: 1,
  },
];

const DEFAULT_STUDENT_BADGES = [
  {
    id: 1,
    student_id: 1,
    badge_id: 1,
    awarded_at: new Date(Date.now() - 30 * 86400000).toISOString(),
    badge: DEFAULT_BADGES[0],
  },
  {
    id: 2,
    student_id: 1,
    badge_id: 2,
    awarded_at: new Date(Date.now() - 20 * 86400000).toISOString(),
    badge: DEFAULT_BADGES[1],
  },
  {
    id: 3,
    student_id: 1,
    badge_id: 3,
    awarded_at: new Date(Date.now() - 5 * 86400000).toISOString(),
    badge: DEFAULT_BADGES[2],
  },
];

const fetcher = (url: string) =>
  apiClient.get(url).then((r) => unwrapApiArray(r));

export function useBadges() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["badges"],
    queryFn: async () => {
      if (user?.isDemo) return DEFAULT_BADGES;
      try {
        return await fetcher("/badges");
      } catch {
        return [];
      }
    },
  });
}

export function useStudentBadges() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["student-badges"],
    queryFn: async () => {
      if (user?.isDemo) return DEFAULT_STUDENT_BADGES;
      try {
        return await fetcher("/badges/my");
      } catch {
        return [];
      }
    },
    staleTime: 1000 * 60 * 2,
  });
}

export function useAdminBadges() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-badges"],
    queryFn: async () => {
      if (user?.isDemo) return DEFAULT_BADGES;
      try {
        return await fetcher("/badges");
      } catch {
        return [];
      }
    },
  });
}

export function useCreateBadge() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      icon: string;
      criteria_type: string;
      criteria_value: number;
    }) => {
      if (user?.isDemo) return { ...data, id: Date.now() };
      const res = await apiClient.post("/badges", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["badges"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
        queryClient.setQueryData(["admin-badges"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      }
    },
  });
}

export function useDeleteBadge() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async (id: number) => {
      if (user?.isDemo) return { success: true, id };
      const res = await apiClient.delete(`/badges/${id}`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["badges"], (old: any[]) =>
          old?.filter((b: any) => Number(b.id) !== Number(id))
        );
        queryClient.setQueryData(["admin-badges"], (old: any[]) =>
          old?.filter((b: any) => Number(b.id) !== Number(id))
        );
      }
    },
  });
}

export { DEFAULT_BADGES, DEFAULT_STUDENT_BADGES };

export function useBadgeProgress(studentHours: number, studentTips: number, studentTasks: number) {
  const { data: allBadges = [] } = useBadges();

  const progressByType: Record<string, { current: number; target: number; badge: any }[]> = {
    hours: [],
    tips: [],
    tasks: [],
    rating: [],
    milestone: [],
  };

  for (const badge of allBadges) {
    let current = 0;
    switch (badge.criteria_type) {
      case "hours":   current = studentHours; break;
      case "tips":    current = studentTips; break;
      case "tasks":   current = studentTasks; break;
      default:        current = 0;
    }
    progressByType[badge.criteria_type]?.push({ current, target: badge.criteria_value, badge });
  }

  const nextByType: Record<string, any> = {};
  for (const [type, items] of Object.entries(progressByType)) {
    const sorted = (items as any[]).sort((a, b) => a.target - b.target);
    nextByType[type] = sorted.find((i) => i.current < i.target) || sorted[0] || null;
  }

  return { progressByType, nextByType };
}
